import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';
import {
  APP_NAME,
  APP_TAGLINE,
  LOGO_SRC,
  getCurrentYear,
} from '../config/branding';
import { LEGAL_NAV_SECTIONS } from './legalNav';
import { FUTURE_LANGUAGES } from './legalConstants';
import './LegalLayout.css';

const LEGAL_MOBILE_BREAKPOINT = 860;

function createResizeObserver(callback) {
  if (typeof ResizeObserver === 'undefined') return null;
  return new ResizeObserver(callback);
}

function scrollLegalPageToTop() {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;
  document.querySelector('.legal-page')?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
  document.querySelector('.legal-main')?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
}

function LanguageSwitcher() {
  const [lang, setLang] = React.useState('en');

  return (
    <div className="legal-lang" aria-label="Language selection">
      <label htmlFor="legal-lang-select" className="legal-lang-label">
        Language
      </label>
      <select
        id="legal-lang-select"
        className="legal-lang-select"
        value={lang}
        onChange={(e) => {
          const next = e.target.value;
          setLang(next);
          if (next !== 'en') {
            // Placeholder for future i18n — English is the only active locale today.
            setLang('en');
          }
        }}
      >
        {FUTURE_LANGUAGES.map((option) => (
          <option key={option.code} value={option.code} disabled={!option.available}>
            {option.label}
            {/* {!option.available ? ' (coming soon)' : ''} */}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function LegalLayout({ title, subtitle, children, embedded = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const stickyHeaderRef = React.useRef(null);
  const [isNarrowViewport, setIsNarrowViewport] = React.useState(false);
  const [mobileSideOpen, setMobileSideOpen] = React.useState(false);
  const [mobileDrawerTop, setMobileDrawerTop] = React.useState(0);

  React.useLayoutEffect(() => {
    scrollLegalPageToTop();
  }, [location.pathname, location.search, location.key]);

  React.useEffect(() => {
    setMobileSideOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${LEGAL_MOBILE_BREAKPOINT}px)`);
    const onChange = () => setIsNarrowViewport(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  React.useEffect(() => {
    if (!mobileSideOpen || !isNarrowViewport) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileSideOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileSideOpen, isNarrowViewport]);

  React.useEffect(() => {
    if (!mobileSideOpen || !isNarrowViewport) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileSideOpen, isNarrowViewport]);

  const updateMobileDrawerTop = React.useCallback(() => {
    const el = stickyHeaderRef.current;
    if (!el) return;
    setMobileDrawerTop(el.getBoundingClientRect().bottom);
  }, []);

  React.useLayoutEffect(() => {
    if (!isNarrowViewport) return undefined;
    updateMobileDrawerTop();
    const onResizeOrScroll = () => updateMobileDrawerTop();
    window.addEventListener('resize', onResizeOrScroll);
    window.addEventListener('scroll', onResizeOrScroll, true);
    let ro = createResizeObserver(onResizeOrScroll);
    if (stickyHeaderRef.current) ro?.observe(stickyHeaderRef.current);
    return () => {
      window.removeEventListener('resize', onResizeOrScroll);
      window.removeEventListener('scroll', onResizeOrScroll, true);
      ro?.disconnect();
    };
  }, [isNarrowViewport, mobileSideOpen, updateMobileDrawerTop]);

  const onSidebarNavClick = React.useCallback(() => {
    if (isNarrowViewport) setMobileSideOpen(false);
  }, [isNarrowViewport]);

  const sidebarClassName = [
    'legal-sidebar',
    isNarrowViewport && !mobileSideOpen ? 'legal-sidebar--mobile-hidden' : '',
    isNarrowViewport && mobileSideOpen ? 'legal-sidebar--mobile-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const mobileNavBar = isNarrowViewport ? (
    <div className="legal-mobile-nav-bar">
      <button
        type="button"
        className="legal-mobile-menu-btn"
        onClick={() => setMobileSideOpen((open) => !open)}
        aria-label={mobileSideOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={mobileSideOpen}
      >
        {mobileSideOpen ? <FiX size={18} aria-hidden /> : <FiMenu size={18} aria-hidden />}
        <span>{mobileSideOpen ? 'Close menu' : 'Menu'}</span>
      </button>
    </div>
  ) : null;

  return (
    <div className={`legal-page${embedded ? ' legal-page--embedded' : ''}`}>
      {!embedded ? (
        <div
          className={`legal-sticky-header${isNarrowViewport && mobileSideOpen ? ' legal-sticky-header--over-drawer' : ''}`}
          ref={stickyHeaderRef}
        >
          <header className="legal-topbar">
            <button type="button" className="legal-logo-btn" onClick={() => navigate('/signin')} aria-label={`${APP_NAME} home`}>
              <img src={LOGO_SRC} alt={APP_NAME} className="legal-logo" />
            </button>
            <div className="legal-topbar-meta">
              <span className="legal-tagline">{APP_TAGLINE}</span>
              <LanguageSwitcher />
            </div>
          </header>
          {mobileNavBar}
        </div>
      ) : isNarrowViewport ? (
        <div
          className={`legal-sticky-header legal-sticky-header--embedded${mobileSideOpen ? ' legal-sticky-header--over-drawer' : ''}`}
          ref={stickyHeaderRef}
        >
          {mobileNavBar}
        </div>
      ) : null}

      <div className={`legal-body${isNarrowViewport && mobileSideOpen ? ' legal-body--sidebar-open' : ''}`}>
        {isNarrowViewport && mobileSideOpen ? (
          <div
            className="legal-mobile-sidebar-backdrop"
            role="presentation"
            aria-hidden
            style={{ top: mobileDrawerTop }}
            onClick={() => setMobileSideOpen(false)}
          />
        ) : null}

        <aside
          className={sidebarClassName}
          aria-label="Documentation and legal navigation"
          aria-hidden={isNarrowViewport && !mobileSideOpen ? true : undefined}
          style={isNarrowViewport && mobileSideOpen ? { top: mobileDrawerTop } : undefined}
        >
          <nav className="legal-sidebar-nav" onClick={onSidebarNavClick}>
            {LEGAL_NAV_SECTIONS.map((section) => (
              <div key={section.title} className="legal-sidebar-section">
                <p className="legal-sidebar-heading">{section.title}</p>
                <ul className="legal-sidebar-list">
                  {section.items.map((item) => {
                    const active = location.pathname === item.path;
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          className={`legal-sidebar-link${active ? ' legal-sidebar-link--active' : ''}`}
                          aria-current={active ? 'page' : undefined}
                        >
                          {item.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>

        </aside>

        <main className="legal-main">
          <header className="legal-main-header">
            <h1 className="legal-title">{title}</h1>
            {subtitle ? <p className="legal-subtitle">{subtitle}</p> : null}
          </header>
          <article className="legal-content">{children}</article>
        </main>
      </div>

      {!embedded ? (
        <footer className="legal-footer">
          <span>© {getCurrentYear()} {APP_NAME}</span>
          <nav className="legal-footer-nav" aria-label="Footer">
            <Link to="/privacy">Privacy</Link>
            <Link to="/products">Products</Link>
            <Link to="/docs">Documentation</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/contact">Contact</Link>
          </nav>
        </footer>
      ) : null}
    </div>
  );
}

export function LegalSection({ id, title, children }) {
  return (
    <section id={id} className="legal-section">
      {title ? <h2 className="legal-section-title">{title}</h2> : null}
      {children}
    </section>
  );
}

export function LegalDisclaimer({ className = '' }) {
  return (
    <aside className={`legal-disclaimer ${className}`.trim()} role="note">
      <strong>Notice:</strong> Policies and documentation on this site may change at any time without
      prior notice. Please review updated materials regularly. 
      {/* 
      <br/>
      For email inquiries:{' '}
      <a href={`mailto:${SUPPORT_EMAIL}`} className="legal-inline-link">
        {SUPPORT_EMAIL}
      </a> */}
    </aside>
  );
}

export function LegalLastUpdated({ date = 'June 27, 2026' }) {
  return (
    <p className="legal-meta">
      Last updated: {date}
    </p>
  );
}
